/**
 * Centralized TanStack Query key factory.
 * All query keys are derived from these factories to ensure
 * consistent cache invalidation and query deduplication.
 */
export const queryKeys = {
  conversations: {
    all: (companyId: string, propertyId: string) =>
      ["conversations", companyId, propertyId] as const,
    list: (companyId: string, propertyId: string, filters: Record<string, unknown>) =>
      ["conversations", companyId, propertyId, "list", filters] as const,
    detail: (companyId: string, conversationId: string) =>
      ["conversations", companyId, conversationId] as const,
  },
  messages: {
    list: (companyId: string, conversationId: string) =>
      ["messages", companyId, conversationId] as const,
  },
  customers: {
    all: (companyId: string, propertyId: string) =>
      ["customers", companyId, propertyId] as const,
    list: (companyId: string, propertyId: string, filters: Record<string, unknown>) =>
      ["customers", companyId, propertyId, "list", filters] as const,
    detail: (companyId: string, customerId: string) =>
      ["customers", companyId, customerId] as const,
    events: (companyId: string, customerId: string) =>
      ["customers", companyId, customerId, "events"] as const,
  },
  analytics: {
    conversations: (companyId: string, propertyId: string, params: Record<string, unknown>) =>
      ["analytics", "conversations", companyId, propertyId, params] as const,
    agents: (companyId: string, propertyId: string, params: Record<string, unknown>) =>
      ["analytics", "agents", companyId, propertyId, params] as const,
    ai: (companyId: string, propertyId: string, params: Record<string, unknown>) =>
      ["analytics", "ai", companyId, propertyId, params] as const,
  },
  tags: {
    list: (companyId: string, propertyId: string) =>
      ["tags", companyId, propertyId] as const,
  },
  cannedResponses: {
    list: (companyId: string, propertyId: string) =>
      ["cannedResponses", companyId, propertyId] as const,
  },
  team: {
    list: (companyId: string) =>
      ["team", companyId] as const,
  },
  kbArticles: {
    all: (companyId: string, propertyId: string) =>
      ["kbArticles", companyId, propertyId] as const,
    list: (companyId: string, propertyId: string, filters: Record<string, unknown>) =>
      ["kbArticles", companyId, propertyId, "list", filters] as const,
    detail: (companyId: string, articleId: string) =>
      ["kbArticles", companyId, articleId] as const,
  },
  kbSources: {
    all: (companyId: string, propertyId: string) =>
      ["kbSources", companyId, propertyId] as const,
    list: (companyId: string, propertyId: string) =>
      ["kbSources", companyId, propertyId, "list"] as const,
  },
  properties: {
    all: (companyId: string) => ["properties", companyId] as const,
    list: (companyId: string) => ["properties", companyId, "list"] as const,
    detail: (companyId: string, propertyId: string) =>
      ["properties", companyId, propertyId] as const,
  },
  widgets: {
    all: (companyId: string, propertyId: string) =>
      ["widgets", companyId, propertyId] as const,
    list: (companyId: string, propertyId: string) =>
      ["widgets", companyId, propertyId, "list"] as const,
  },
  integrations: {
    all: (companyId: string, propertyId: string) =>
      ["integrations", companyId, propertyId] as const,
    list: (companyId: string, propertyId: string) =>
      ["integrations", companyId, propertyId, "list"] as const,
  },
  apiKeys: {
    all: (companyId: string) => ["apiKeys", companyId] as const,
    list: (companyId: string) => ["apiKeys", companyId, "list"] as const,
  },
  // Platform-admin queries deliberately do NOT embed the current companyId —
  // they span all companies.
  admin: {
    stats: () => ["admin", "stats"] as const,
    companies: (search: string, page: number) =>
      ["admin", "companies", search, page] as const,
    company: (companyId: string) => ["admin", "company", companyId] as const,
    companyUsers: (companyId: string) =>
      ["admin", "company", companyId, "users"] as const,
    users: (search: string, page: number) =>
      ["admin", "users", search, page] as const,
  },
};
